import { Store, StorageType, Analytics, Purchase } from '@nakedplatform/support';
import axios from 'axios';
import platform from 'platform';
import { formatISO } from 'date-fns';
import { getEventTime } from './timers';

import { getLocalUtmParams } from './user';
const PEACH_REFERRAL_LOOKUP = 'oppwa.com';
const FIRST_SEEN_PROPERTIES = ['First Seen Date', 'First Seen on Web Date', 'First Seen Source'];

/**
 * Resolves a promise after the supplied milliseconds
 *
 * @param ms - The amount of delay (in milliseconds) before function resolves
 */
export async function delay(ms: number) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}
/**
 * Retrieves mixpanel id
 */
export function getMixpanelId() {
  return window.mixpanel && window.mixpanel.get_distinct_id && window.mixpanel.get_distinct_id();
}
/**
 * Retrieves google id
 */
export function getGoogleId() {
  return new Promise<string>(resolve => {
    let clientId: string;

    if (!window.ga) {
      return resolve(clientId);
    }

    // This will throw an error if getAll method doesn't exist
    return resolve(window.ga.getAll()[0].get('clientId'));

    // TODO: Investigate google analytics
    // window.ga(tracker => {
    //   clientId = tracker.get('clientId');
    //   resolve(clientId);
    // });
  });
}
/**
 * Retrieves analytics id
 */
export function getAnalyticsId() {
  return new Promise<string>(resolve => {
    if (window.analytics) {
      window.analytics.ready(() => {
        const userId = window.analytics.user().id();

        resolve(userId);
      });
    } else {
      resolve(undefined);
    }
  });
}
/**
 * Retrieves segment id
 */
export function getSegmentId() {
  try {
    return window.analytics.user().id();
  } catch (e) {
    return '';
  }
}
/**
 * Retrieves Segment traits
 */
export function getSegmentTraits() {
  try {
    return window.analytics.user().traits();
  } catch (e) {
    return {};
  }
}
/**
 * Stores the referrer to storage
 */
export function storeReferrer() {
  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);
    const referrer = document.referrer || '$direct';

    if (!referrer.includes(PEACH_REFERRAL_LOOKUP)) {
      storage.setItem('referrer', referrer);
    }
  }
}
/**
 * Retrieves the referrer from local storage
 */
export function getReferrer() {
  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);

    return storage.getItem('referrer') || '';
  }

  return '';
}
/**
 * Fetches ip address from ipify
 */
export function fetchIpAddress(): Promise<string> {
  return axios
    .get('https://api.ipify.org/')
    .then(({ data }) => data)
    .catch(() => '');
}
/**
 * Retrieves ip address from storage if stored, otherwise retrieves from ipify
 */
export async function getIpAddress() {
  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);
    const ip = storage.getItem('ip');

    if (ip) {
      return ip;
    }

    const address = await fetchIpAddress();

    storage.setItem('ip', address);

    return address;
  }

  return fetchIpAddress();
}
/**
 * Retrieves browser context
 */
export async function getContext() {
  const referrer = getReferrer();
  const ip = await getIpAddress();
  const platformParams = {
    browser: platform.name,
    browserVersion: platform.version,
    operatingSystem: platform.os.family,
    operatingSystemVersion: platform.os.version,
    userAgent: window.navigator.userAgent,
  };

  return {
    ip,
    ...platformParams,
    referrer,
  };
}
/**
 * Retrieves analytics data from mixpanel, google and segment
 */
export async function getAnalyticsData() {
  const mixpanelDistinctId = getMixpanelId();
  const googleAnalyticsClientId = await getGoogleId();

  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);
    const { referrer, ...firstSeenParams } = Store.getItems(storage, [
      ...FIRST_SEEN_PROPERTIES,
      'referrer',
    ]);
    const {
      paramsLastTouch: lastTouchUtmParams,
      paramsFirstTouch: firstTouchUtmParams,
    } = getLocalUtmParams();
    const attributes = {
      ...lastTouchUtmParams,
    };

    const mixpanelSetOnceAttributes = {
      ...firstSeenParams,
      ...firstTouchUtmParams,
      $initial_referrer: referrer,
    };

    return {
      attributes,
      mixpanelDistinctId,
      googleAnalyticsClientId,
      mixpanelSetOnceAttributes,
      context: await getContext(),
    };
  }

  return {
    mixpanelDistinctId,
    googleAnalyticsClientId,
  };
}
/**
 * Flattens an object
 *
 * @param input - Data to flatten
 */
function flatten(input: Record<string, any>) {
  const keys = Object.keys(input);

  return keys.reduce((memo, key) => {
    const value = input[key];
    const data =
      value.constructor === Object
        ? flatten(value)
        : {
            [key]: value,
          };

    return {
      ...memo,
      ...data,
    };
  }, {});
}
/**
 * Retrieves common purchase event data
 *
 * @param event - The purchase event data
 */
export async function getQuoteEvent(event: QuoteEvent) {
  const { userId, email, itemType, isQuote } = event;
  const timestamp = formatISO(new Date());
  const { mixpanelSetOnceAttributes, context, attributes } = await getAnalyticsData();

  const eventProperties = {
    ...window.segmentAppContext,
    ...Analytics.getOptions(),
    itemType,
    path: window.location.pathname,
    ...getEventTime(isQuote),
  };

  return {
    userId,
    attributes: flatten({ email, attributes }),
    mixpanelSetOnceAttributes: flatten(mixpanelSetOnceAttributes),
    serverSide: true,
    excludeOptions: true,
    allowEmailAsUserId: false,
    eventProperties,
    timestamp,
    context: flatten(context),
  };
}
export interface QuoteEvent extends Purchase {
  isQuote?: boolean;
}
